import { action, computed } from 'mobx'
import { parse as parseMinidown } from '@/minidown/parser'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { NotificationStore } from '@/features/activity/NotificationStore'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import type {
  SnackbarErrorHandlerFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import type { Notification } from '@/features/activity/Notification'
import { ViewModelList } from '@/infra/view-model-list'
import { task } from 'mobx-task'
import { memoize } from '@/utils/memoize-util'

export class ActivityViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _viewModels

  readonly scroller = new InfiniteScrollProvider({
    limit: 50,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createSnackbarRetryWrapper(
        'Unable to load notifications',
        signal,
      )

      return withRetry(() =>
        this._notificationStore.listNotifications(paging, signal),
      )
    },
  })

  constructor(
    private readonly _notificationStore: NotificationStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
  ) {
    this.fetchNotifications = memoize(this.fetchNotifications.bind(this), {
      promise: ([signal]) => signal,
    })

    this._viewModels = new ViewModelList({
      models: () => this.scroller.items.slice(),
      create: (m) => new NotificationItemViewModel(m, () => this._markRead(m)),
    })
    this._lifecycle.manage(this._viewModels)

    // Always keep the scroller in sync since this VM is a singleton,
    // and we want to cache the loaded notifications.
    syncInfiniteScrollerItems({
      scroller: this.scroller,
      additions$: _notificationStore.newNotifications$,
      removals$: _notificationStore.notificationRemoved$,
    }).subscribe()
  }

  @computed
  get notifications() {
    const recent: NotificationItemViewModel[] = []
    const past: NotificationItemViewModel[] = []

    for (const item of this._viewModels.items) {
      if (item.notification.status === 'Read') {
        past.push(item)
      } else {
        recent.push(item)
      }
    }

    // Sort by dispatch time, descending.
    const comparer = (
      a: NotificationItemViewModel,
      b: NotificationItemViewModel,
    ) =>
      b.notification.dispatchedAt.valueOf() -
      a.notification.dispatchedAt.valueOf()
    recent.sort(comparer)
    past.sort(comparer)

    return { empty: recent.length === 0 && past.length === 0, recent, past }
  }

  activate() {
    this._lifecycle.activate()
    void this.fetchNotifications(this._lifecycle.signal)
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  fetchNotifications(signal?: AbortSignal) {
    return this.scroller.fetch({ signal, staleWhileRevalidate: true })
  }

  markAllRead = task.resolved(() => {
    const notifications = this.notifications.recent.map((vm) => vm.notification)

    return this._notificationStore
      .markRead(notifications, this._lifecycle.signal)
      .catch(
        this._createSnackbarErrorHandler(
          `Marking ${notifications.length} notifications as read failed`,
        ),
      )
  })

  /**
   * Marks the notification as read.
   * @param notification
   * @private
   */
  private _markRead(notification: Notification) {
    if (notification.status !== 'Read') {
      void this._notificationStore.markRead([notification])
    }
  }
}

export class NotificationItemViewModel {
  constructor(
    public notification: Notification,
    private readonly _markRead: () => unknown,
  ) {}

  @computed
  get parsedText() {
    return parseMinidown(this.notification.message)
  }

  @computed
  get plainText() {
    return this.notification.plainText
  }

  @action.bound
  markRead() {
    this._markRead()
  }
}
